import * as PIXI from "pixi.js";
import {
  logoPotStage,
  onLogoPotChange,
  LOGO_POT_STAGES,
  LOGO_POT_WIN,
} from "../../../Games/Shared/logoPot";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Skin } from "@esotericsoftware/spine-core";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import { Settings } from "../../../settings";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { formatToCustomLocale } from "../../../utilities";
import {
  playSceneAccent,
  playSceneAccentForWindow,
  stopSceneAccents,
} from "../../../Games/Shared/sceneAccents";

// ============================================================================
// SPINE CONTRACT
//
// Everything this controller needs to know about the skeleton lives here. When
// the Mad Labs game_view is re-exported with different names, this block is the
// only thing that changes.
//
// Each entry is a CANDIDATE LIST, tried in order, first hit wins. That is not
// indecision — it lets the component keep working across a re-export that
// renames one thing, instead of silently rendering nothing.
// ============================================================================
const SPINE_ASSETS = {
  SKELETON: "game_view-skeleton",
  ATLAS: "game_view-atlas",
};

// The logo spine injected into LOGO_SLOTS. Default skin only — its own
// landscape/portrait skins are not used; the scene's slot places it.
//
// The logo is the Pot (GDD §5): the fire in its cup is a separate loop on its
// own track, over the logo's idle. logo_idle draws no fire at all, and GDD
// stage 1 is a small flame "in beginning stage" — so the resting logo is
// logo_idle + logo_lvl1. lvl2–lvl4 (the fire growing) are not driven yet.
const LOGO_ASSETS = {
  SKELETON: "logo-skeleton",
  ATLAS: "logo-atlas",
  // The level clip carries EVERYTHING (idle included) — the animator joined
  // them (2026-09-29): play ONE level clip, looped. Stage 1 for now.
  // The fire stage is driven now (setLogoStage, Games/Shared/logoPot) — this
  // list is only a fallback if the stage clips are missing.
  LEVEL_ANIMATIONS: ["logo_lvl1"],
  // LEVEL_ANIMATIONS: ["logo_lvl4"], // TEMP: lvl4 for measuring (Vladimir, 2026-09-29) — back to "logo_lvl1"
  LEVEL_TRACK: 0,
  // Seconds of blend between one fire clip and the next. A stand-in until
  // the animator connects them: straight cuts popped, and a jump of more than
  // one stage (1 -> 4 on a Hold & Win trigger) has no clip joining the two
  // fires at all (Vladimir, 2026-09-29: "temporarily blend it").
  LEVEL_MIX: 0.4,
  // Was: logo_idle on track 0 + logo_lvl1 on track 1.
  // IDLE_ANIMATIONS: ["logo_idle"],
  // FIRE_ANIMATIONS: ["logo_lvl1"],
  // IDLE_TRACK: 0,
  // FIRE_TRACK: 1,
  // A coin's trail landing in the cup (GDD §5.1) — over the level clip.
  HIT_ANIMATIONS: ["logo_hit"],
  HIT_TRACK: 2,
  // The WIN blast (GDD: "small blast animation around the fire and logo") —
  // its own fire slots, so it plays over the level clip on its own track.
  BLAST_ANIMATIONS: ["logo_blast"],
  BLAST_TRACK: 1,
  // The fire bowl on top of the logo — where the coins' trails fall in.
  FIRE_BONES: ["logo_fire_00000", "logo_fire_00001"],
};

// The jackpot plates' hit clips (jackpot_<tier>_hit, base game_view 2026-09-30)
// — each tier on its own track above idle (0), accents (1, 2) and the
// anticipation (3).
const JACKPOT_HIT_TIERS = ["mini", "minor", "midi", "major", "grand", "royal"];
const JACKPOT_HIT_TRACK_BASE = 4;
// A lit plate's fade back out at the next spin, s.
const JACKPOT_HIT_RELEASE_S = 0.25;
// The Random Pot's logo flight over the reels (logo_reel_*) — above the
// jackpot hits' tracks (4–9).
const LOGO_REEL_TRACK = 10;

// The accent tables and the three functions behind playAccent /
// playAccentForWindow / stopAccent. Re-exported so a caller that already names
// this scene keeps one import.
export { SCENE_ACCENTS, SCENE_ACCENT_TRACKS } from "../../../Games/Shared/sceneAccents";

// The jackpot plates' value text (setJackpotValues), in spine units — the
// plate slot scales it. MAX_WIDTH keeps the widest value inside the plates.
const JACKPOT_PLATE_TEXT = { FONT_SIZE: 52, MAX_WIDTH: 200 };
// Per plate — the plates are not one size (art widths: royal 300, grand 287,
// major / midi 269, minor / mini 231), so one size overfilled the small ones
// (Vladimir, 2026-09-30: "mini minor should be smaller"). Font by the plate's
// rank, width held to ~80% of its art.
const JACKPOT_PLATE_TEXT_BY_TIER = {
  royal: { FONT_SIZE: 52, MAX_WIDTH: 240 },
  grand: { FONT_SIZE: 52, MAX_WIDTH: 230 },
  major: { FONT_SIZE: 46, MAX_WIDTH: 215 },
  midi: { FONT_SIZE: 46, MAX_WIDTH: 215 },
  minor: { FONT_SIZE: 40, MAX_WIDTH: 185 },
  mini: { FONT_SIZE: 40, MAX_WIDTH: 185 },
};

/** Prefix for this scene's warnings. */
const TAG = "GameViewSimple";

const SPINE_CONTRACT = {
  // The single looping animation. Zeus called it base_idle.
  IDLE_ANIMATIONS: ["idle", "base_idle", "main_idle", "Main_bg_idle"],

  // Layout skins, one per orientation. Combined with "default" — see applySkin.
  LANDSCAPE_SKINS: ["resize/landscape", "landscape"],
  PORTRAIT_SKINS: ["resize/portrait", "portrait"],

  // Where the spinning reels go, and where the spine overlay above them goes.
  //
  // Both are SLOTS. The artist places them in the draw order and we inject at
  // that exact point — in Mad Labs they sit directly above reel_bg_50%2 and
  // below logo, which is precisely right, so there is nothing to tune.
  //
  // Both slots are EMPTY markers with no attachment in any skin. That is fine:
  // spine-pixi only ties a slot object's visibility to an attachment when
  // followAttachmentTimeline is set, and it is not.
  // (Cleopatra's new exports: the reel slots carry the reel background and
  // frame images; spine-pixi draws the slot object on top of the image.)
  //
  // The animator's reel slots (base: *_maingame, free spins: *_freegame), both
  // moved to the frame window's centre in JSON (animator item 21). The old
  // placeholder names stay for the Hold & Win spine.
  REEL_SLOTS: [
    "attachment_reelbg_maingame",
    "attachment_reelbg_freegame",
    "attachment_reelbg_hnw",
    "attachment_slot_placeholder",
  ],
  OVERLAY_SLOTS: [
    "attachment_reelframe_maingame",
    "attachment_reelframe_freegame",
    "attachment_reelframe_hnw",
    "attachment_slot_placeholder_front",
  ],

  // Per-reel scatter anticipation (GDD §6.1). The export carries it for ONE
  // column only, on the `anticipation` bone (a child of `reels`, over the
  // middle reel); no clip keys that bone, so it is moved to whichever reel is
  // anticipating before a clip plays. Only one reel at a time — which is how
  // the chain runs anyway. Its own track, clear of the idle (0) and the
  // accents (1, 2). ANTICIPATION_SLOT_PREFIX names every slot the three clips
  // draw, so they can be put back to setup (hidden) when the chain ends.
  ANTICIPATION_BONES: ["anticipation"],
  ANTICIPATION_IN: ["anticipation_in"],
  ANTICIPATION_LOOP: ["anticipation_loop"],
  ANTICIPATION_OUT: ["anticipation_out"],
  ANTICIPATION_SLOT_PREFIX: "ant_",
  ANTICIPATION_TRACK: 3,
  // The new exports (2026-09-29): the anticipation is its OWN spine, played
  // in this slot. Its column is centred on its origin; the slot's bone is
  // placed over the reel window (the reel slot's bone) and stepped per reel.
  ANTICIPATION_SLOTS: ["attachment_anticipation"],
  ANTICIPATION_SPINE: { SKELETON: "anticipation-skeleton", ATLAS: "anticipation-atlas" },
  // The reel anticipation (animator, 2026-09-29): a frame over the WHOLE board,
  // its own spine, in this slot (drawn last — above the reel frame). Up for
  // the whole chain: in → loop at its start, out when it ends. Where Mad Labs
  // lit its reel accent (reel_highlight) when anticipation started.
  REEL_ANTICIPATION_SLOTS: ["attachment_reel_anticipation"],
  REEL_ANTICIPATION_SPINE: {
    SKELETON: "reel_anticipation-skeleton",
    ATLAS: "reel_anticipation-atlas",
  },
  REEL_ANTICIPATION_IN: ["reel_anticipation_in"],
  REEL_ANTICIPATION_LOOP: ["reel_anticipation_loop"],
  REEL_ANTICIPATION_OUT: ["reel_anticipation_out"],

  // Where the game logo goes. The slot's bone carries the position and scale
  // for each orientation (the layout skins switch it, like the jackpots), and
  // the logo spine is centred on its own origin, so nothing is tuned in code.
  // attachment_logo: the workshop game view, one slot moved by resize/landscape and resize/portrait.
  LOGO_SLOTS: ["logo_placeholder", "attachment_logo"],
  // New game view: one logo slot per layout.
  LOGO_SLOTS_LANDSCAPE: ["attachment_logo_landscape"],
  LOGO_SLOTS_PORTRAIT: ["attachment_logo_portrait"],

  // Fallback only, for a skeleton that marks the reel position with a bare bone
  // instead of a slot. Then the container has to follow the bone by hand and
  // carry a forced zIndex — see _attachToBone.
  REEL_BONES: ["attachment_slot_placeholder"],
  REEL_BEHIND_SLOTS: ["reel_bg_50%2", "main_reel_bg"],

  // The bone that parents the whole reel block — frame, keyboard, screen mesh,
  // background plate AND the injection point. Scaling it resizes the board
  // relative to the room around it. See GAME_VIEW_REEL_SCALE_CONFIG.
  REEL_BLOCK_BONES: ["reels"],
};

// ============================================================================
// SCENE OFFSET — per layout
//
// Moves the WHOLE scene: background art, reel frame and the injected reels all
// together, because they are one spine.
//
// Keyed by LAYOUT, not by device. Same ten keys as
// PixiSettings.REELSET_LAYOUT_CONFIGS, so the padding table and this table line
// up one-to-one. Device alone could not express this — it cannot tell a small
// desktop from an ultra-wide one, and those need different offsets.
//
// Units are multiples of symbolHeight, so an offset holds its proportion when
// the board resizes.
//
//   y: POSITIVE pushes the scene UP.     ← same sign as GAME_CONTAINER_OFFSET_CONFIG
//   x: POSITIVE pushes the scene RIGHT.
//
// A plain number is shorthand for y-only, which is the common case.
// ============================================================================
// Cleopatra's Fire: CENTER_FIX_X moves every layout sideways, in SPINE units
// (converted to cells here), so it scales with the scene. 0 = no move — tried
// 2..8 right by eye, 2026-09-28, and none was right. Both rooms read this
// table (FreeSpinsGameViewController extends this class), so they move together.
const CENTER_FIX_X = 0 / Settings.SYMBOL_HEIGHT;

export const GAME_VIEW_OFFSET_CONFIG = {
  // Portrait: the 9:16 view's centre is 110.9 units below the stage centre,
  // so the scene moves up 110.9 / 185 cells.
  MOBILE_SMALL_PORTRAIT: { x: CENTER_FIX_X, y: 0.599 },
  MOBILE_SMALL_LANDSCAPE: { x: CENTER_FIX_X, y: 0 },
  MOBILE_PORTRAIT: { x: CENTER_FIX_X, y: 0.599 },
  MOBILE_LANDSCAPE: { x: CENTER_FIX_X, y: 0 },
  TABLET_PORTRAIT: { x: CENTER_FIX_X, y: 0.599 },
  TABLET_LANDSCAPE: { x: CENTER_FIX_X, y: 0 },
  SMALL_DESKTOP: { x: CENTER_FIX_X, y: 0 },
  DESKTOP: { x: CENTER_FIX_X, y: 0 },
  WIDE_DESKTOP: { x: CENTER_FIX_X, y: 0 },
  ULTRA_WIDE_DESKTOP: { x: CENTER_FIX_X, y: 0 },
};

// ============================================================================
// SCENE ZOOM — per layout
//
// Multiplier on top of the engine's own scale. 1.0 is untouched.
//
// This is the knob for "make the whole thing bigger". The engine's scale comes
// from the reel-area fit and is CAPPED AT 1.0 below a 2800px-wide screen, so
// once the fit reaches that cap there is no headroom left — lowering
// SYMBOL_WIDTH/HEIGHT buys nothing more. This multiplier is ours and ignores
// the cap.
//
// It zooms EVERYTHING uniformly: background, reel frame and the reels. The
// injected containers are counter-scaled by 1 / symbolScale, so the zoom
// passes straight through to them rather than being cancelled — which is why
// the board grows with the scene instead of staying put inside a bigger frame.
//
// Same ten keys as GAME_VIEW_OFFSET_CONFIG.
// ============================================================================
// Cleopatra's Fire: ALL 1.0. Mad Labs zoomed the scene and shrank the reel
// block by the matching 1/Z (see below) to grow its background; this game's
// background and reels scale together, so neither knob is used.
export const GAME_VIEW_ZOOM_CONFIG = {
  MOBILE_SMALL_PORTRAIT: 1.0,
  MOBILE_SMALL_LANDSCAPE: 1.0,
  MOBILE_PORTRAIT: 1.0,
  MOBILE_LANDSCAPE: 1.0,
  TABLET_PORTRAIT: 1.0,
  TABLET_LANDSCAPE: 1.0,
  SMALL_DESKTOP: 1.0,
  DESKTOP: 1.0,
  WIDE_DESKTOP: 1.0,
  ULTRA_WIDE_DESKTOP: 1.0,
};

const resolveZoom = (layout) => GAME_VIEW_ZOOM_CONFIG[layout] ?? 1;

// ============================================================================
// REEL BLOCK SCALE — per layout
//
// Resizes the reel block INSIDE the scene: the frame, the keyboard, the screen
// mesh, the background plate and the reels themselves. 1.0 is the skeleton as
// the artist exported it.
//
// This is the ratio knob, and it is the opposite of the zoom above:
//
//   GAME_VIEW_ZOOM_CONFIG      grows the whole scene, room included
//   GAME_VIEW_REEL_SCALE_CONFIG  shrinks the board WITHIN the room
//
// Use them together — shrink the board, then zoom the scene — to make the
// background fill the screen while the board stays the size you want.
//
// Applied to the `reels` bone at runtime rather than baked into the spine, so
// an artist re-export cannot silently drop it.
// ============================================================================
// Cleopatra's Fire: ALL 1.0 — the reel block is never scaled apart from the
// background. See the zoom above.
export const GAME_VIEW_REEL_SCALE_CONFIG = {
  MOBILE_SMALL_PORTRAIT: 1.0,
  MOBILE_SMALL_LANDSCAPE: 1.0,
  MOBILE_PORTRAIT: 1.0,
  MOBILE_LANDSCAPE: 1.0,
  TABLET_PORTRAIT: 1.0,
  TABLET_LANDSCAPE: 1.0,
  SMALL_DESKTOP: 1.0,
  DESKTOP: 1.0,
  WIDE_DESKTOP: 1.0,
  ULTRA_WIDE_DESKTOP: 1.0,
};

const resolveReelScale = (layout) => GAME_VIEW_REEL_SCALE_CONFIG[layout] ?? 1;

/**
 * Resolve the offset for a layout into { x, y } multipliers.
 * Accepts a plain number (y-only) or an { x, y } object.
 */
const resolveOffset = (layout) => {
  const entry = GAME_VIEW_OFFSET_CONFIG[layout];
  if (typeof entry === "number") return { x: 0, y: entry };
  return { x: entry?.x ?? 0, y: entry?.y ?? 0 };
};

/**
 * GameViewSimpleController — the Mad Labs scene.
 *
 * One spine, one looping animation, one skin per orientation, and two
 * injection points: the spinning reels and the spine overlay above them.
 * That is the whole job.
 *
 * It is deliberately NOT the Zeus GameViewController. That one also owns
 * jackpot meters, pot controllers and their text labels, a coin counter, a
 * respin counter, rain, a cloud transition, anticipation, a bone-level shake
 * and three kinds of overlay spine. None of that exists in this game. The old
 * pair is kept for reference until this one is proven.
 *
 * Both injection points are spine SLOTS, so the artist owns the layering and we
 * inherit it. In the Mad Labs skeleton they sit at draw-order 79 and 80 —
 * directly above reel_bg_50%2 and below logo — which is exactly right, so there
 * is nothing here to tune. A bone-following fallback exists for a skeleton that
 * marks the spot with a bare bone instead; see _attachToBone.
 */
class GameViewSimpleController {
  // Which spine this controller drives and where it publishes its two slots.
  // A room built on the SAME skeleton (FreeSpinsGameViewController) extends
  // this class and overrides only this block — the skins, idle, scaling and
  // injection are shared.
  static SCENE = {
    TAG,
    SKELETON: SPINE_ASSETS.SKELETON,
    ATLAS: SPINE_ASSETS.ATLAS,
    // Spine name for SpineAtlasUploader (the assets.js entry name).
    SPINE_NAME: "game_view",
    REEL_SET_KEY: "gameView.reelSet",
    OVERLAY_REEL_SET_KEY: "gameView.overlayReelSet",
    // Optional per-scene overrides. IDLE_ANIMATIONS replaces the contract's
    // idle candidates (a room whose export names its idle differently);
    // HAS_LOGO false skips the logo injection for a room with no logo slot.
    IDLE_ANIMATIONS: null,
    HAS_LOGO: true,
  };

  constructor(
    container,
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    slotRegistry,
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.layout = layout;
    this.spine = null;

    this._registerSlot = slotRegistry?.registerSlot ?? null;
    this._unregisterSlot = slotRegistry?.unregisterSlot ?? null;

    this.reelSlotContainer = null;
    this.overlayReelSlotContainer = null;
    this._reelBone = null;
    this.logoSpine = null;
    this._anticipationBaseX = null;

    this._currentLayout = null;

    this.initializeSpine();
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  initializeSpine() {
    this.spine = this.createSpine();
    if (!this.spine) return;

    this.spine.position.set(0, 0);
    this.spine.zIndex = 1;
    this.spine.visible = true;
    this.container.addChild(this.spine);

    this.applySkin(this.resolveLayoutSkin());
    this.playIdle();
    this.applyResponsiveScaling();
    this.applyReelBlockScale();
    this.attachSlotContainers();
    if (this.constructor.SCENE.HAS_LOGO !== false) this.attachLogo();
    this.attachAnticipation();
    this.attachReelAnticipation();

    // Push the atlas pages to the GPU before the first frame that needs them,
    // so the scene does not hitch on its own reveal.
    requestAnimationFrame(() => {
      SpineAtlasUploader.upload(this.constructor.SCENE.SPINE_NAME);
    });
  }

  createSpine() {
    const { TAG: tag, SKELETON, ATLAS } = this.constructor.SCENE;
    try {
      const assets = PIXI.Assets.cache;
      if (!assets.has(SKELETON) || !assets.has(ATLAS)) {
        console.warn(`[${tag}] Spine assets not in cache:`, SKELETON, ATLAS);
        return null;
      }

      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });

      // skeleton and state are siblings on the Spine display object, not nested.
      if (!spine?.skeleton || !spine?.state || !spine.skeleton.data) {
        console.warn(`[${tag}] Spine created but incomplete`);
        return null;
      }

      return spine;
    } catch (error) {
      console.error(`[${tag}] Failed to create spine:`, error);
      return null;
    }
  }

  // ==========================================================================
  // NAME RESOLUTION — everything below reads the contract, never a literal
  // ==========================================================================

  _firstAnimation(candidates) {
    const data = this.spine?.skeleton?.data;
    if (!data) return null;
    for (const name of candidates) {
      if (data.findAnimation(name)) return name;
    }
    return null;
  }

  _firstSkin(candidates) {
    const data = this.spine?.skeleton?.data;
    if (!data) return null;
    for (const name of candidates) {
      if (data.findSkin(name)) return name;
    }
    return null;
  }

  _firstBone(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates) {
      const bone = skeleton.findBone(name);
      if (bone) return bone;
    }
    return null;
  }

  _firstSlotName(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /**
   * Draw-order index of a slot. Used to compute a zIndex that survives a
   * re-export, where absolute slot numbers change but the order does not.
   */
  _getSlotIndex(slotName) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton || !slotName) return 0;
    const slot = skeleton.findSlot(slotName);
    return slot ? skeleton.slots.indexOf(slot) : 0;
  }

  // ==========================================================================
  // SKIN — one per orientation, combined with "default"
  // ==========================================================================

  /**
   * Apply the layout skin on top of "default".
   *
   * They are combined rather than swapped: "default" carries the art that does
   * not change with orientation, and the layout skin only re-positions. Setting
   * the layout skin alone would drop everything default holds.
   */
  applySkin(layout) {
    const data = this.spine?.skeleton?.data;
    if (!data) return;

    const defaultSkin = data.findSkin("default");
    if (!defaultSkin) {
      console.warn(
        `[${this.constructor.SCENE.TAG}] No default skin — cannot apply skins`,
      );
      return;
    }

    const combined = new Skin("combined");
    combined.addSkin(defaultSkin);

    const layoutSkin = layout ? data.findSkin(layout) : null;
    if (layoutSkin) combined.addSkin(layoutSkin);

    this.spine.skeleton.setSkin(combined);
    this.spine.skeleton.setSlotsToSetupPose();

    this._currentLayout = layout;
  }

  /** The layout skin name for the current device and orientation. */
  resolveLayoutSkin() {
    const isPortrait =
      this.device !== PixiSettings.DEVICE.DESKTOP &&
      this.orientation === PixiSettings.ORIENTATION.PORTRAIT;

    return this._firstSkin(
      isPortrait
        ? SPINE_CONTRACT.PORTRAIT_SKINS
        : SPINE_CONTRACT.LANDSCAPE_SKINS,
    );
  }

  setOrientation(orientation) {
    this.orientation = orientation;
    const layout = this.resolveLayoutSkin();
    if (layout === this._currentLayout) return;
    this.applySkin(layout);
    // New game view: the logo has a slot per layout — move it across.
    const logoSlot = this._logoSlotName();
    if (this.logoSpine && logoSlot) this.spine.addSlotObject(logoSlot, this.logoSpine);
  }

  /** The logo's slot: the layout's own (new game view), else the shared one. */
  _logoSlotName() {
    const isPortrait =
      this.device !== PixiSettings.DEVICE.DESKTOP &&
      this.orientation === PixiSettings.ORIENTATION.PORTRAIT;
    return (
      this._firstSlotName(
        isPortrait ? SPINE_CONTRACT.LOGO_SLOTS_PORTRAIT : SPINE_CONTRACT.LOGO_SLOTS_LANDSCAPE,
      ) || this._firstSlotName(SPINE_CONTRACT.LOGO_SLOTS)
    );
  }

  // ==========================================================================
  // ANIMATION — there is only one
  // ==========================================================================

  playIdle() {
    if (!this.spine?.state) return;
    const idle = this._firstAnimation(
      this.constructor.SCENE.IDLE_ANIMATIONS ?? SPINE_CONTRACT.IDLE_ANIMATIONS,
    );
    if (!idle) {
      console.warn(
        `[${this.constructor.SCENE.TAG}] No idle animation found in skeleton`,
      );
      return;
    }
    // Skip if already running, so a resize cannot restart it from frame 0.
    if (this.spine.state.getCurrent(0)?.animation?.name === idle) return;
    this.spine.state.setAnimation(0, idle, true);
  }

  // ==========================================================================
  // ACCENTS — clips played OVER the idle, on their own tracks
  //
  // The behaviour, the comments and every decision behind them live in
  // Games/Shared/sceneAccents. ONE copy for both rooms: the base game's rig and
  // the bonus room's are byte-identical exports of the same skeleton, so an
  // accent that is right here is right there.
  // ==========================================================================

  // ── OFF for Cleopatra's Fire (Vladimir, 2026-09-30) ── the accents are Mad
  // Labs' rig clips (rat/rat_shocked, mystery waits, …); these rooms have
  // none, so every call only logged "No accent animation found". Both return
  // false, as a missing clip always did — no caller uses the value.
  static ACCENTS_ENABLED = false;

  /** @see playSceneAccent */
  playAccent(candidates, options) {
    if (!this.constructor.ACCENTS_ENABLED) return false;
    return playSceneAccent(this.spine, candidates, {
      tag: this.constructor.SCENE.TAG,
      ...options,
    });
  }

  /** @see playSceneAccentForWindow */
  playAccentForWindow(candidates, windowMs, options) {
    if (!this.constructor.ACCENTS_ENABLED) return false;
    return playSceneAccentForWindow(this.spine, candidates, windowMs, {
      tag: this.constructor.SCENE.TAG,
      ...options,
    });
  }

  /** @see stopSceneAccents */
  stopAccent() {
    stopSceneAccents(this.spine);
  }

  // ==========================================================================
  // ANTICIPATION — one reel at a time, driven by ReelControlContext
  // ==========================================================================

  /**
   * The standalone anticipation spine (new exports), in the scene's
   * `attachment_anticipation` slot. Only when the scene does NOT carry the
   * clips itself — the older spines keep their built-in path.
   *
   * Wrapped in a holder: spine-pixi rewrites a slot object's own visible /
   * alpha every frame, so the spine inside is what gets shown and hidden.
   */
  attachAnticipation() {
    const tag = this.constructor.SCENE.TAG;
    const slotName = this._firstSlotName(SPINE_CONTRACT.ANTICIPATION_SLOTS);
    if (!slotName || this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_LOOP)) return;

    const { SKELETON, ATLAS } = SPINE_CONTRACT.ANTICIPATION_SPINE;
    const assets = PIXI.Assets.cache;
    if (!assets.has(SKELETON) || !assets.has(ATLAS)) {
      console.warn(`[${tag}] Anticipation spine not in cache`);
      return;
    }
    try {
      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });
      spine.name = "anticipation";
      spine.visible = false;
      const holder = new PIXI.Container();
      holder.name = "anticipation_slot_content";
      holder.addChild(spine);
      this.spine.addSlotObject(slotName, holder);
      this.anticipationSpine = spine;
      this._anticipationSlotName = slotName;
    } catch (error) {
      console.error(`[${tag}] Failed to create anticipation spine:`, error);
    }
  }

  /**
   * Where the anticipation clips play: the standalone spine (track 0), or
   * the scene's own clips (ANTICIPATION_TRACK) on the older spines.
   */
  _anticipationHost() {
    if (this.anticipationSpine) return { spine: this.anticipationSpine, track: 0 };
    return { spine: this.spine, track: SPINE_CONTRACT.ANTICIPATION_TRACK };
  }

  _anticipationClip(host, candidates) {
    const data = host.spine?.skeleton?.data;
    return candidates.find((name) => data?.findAnimation(name)) || null;
  }

  /**
   * Move the anticipation bone over `reelIndex` and play in → loop there.
   * The chain calls it once per reel, after the previous reel's out.
   */
  playAnticipation(reelIndex) {
    if (this.anticipationSpine) {
      this._playStandaloneAnticipation(reelIndex);
      return;
    }
    const state = this.spine?.state;
    const bone = this._firstBone(SPINE_CONTRACT.ANTICIPATION_BONES);
    const inClip = this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_IN);
    const loopClip = this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_LOOP);
    if (!state || !bone || !loopClip) return;

    // The export places the bone over the middle reel. Reels are one cell
    // apart; a cell is symbolWidth px, and the spine is drawn at symbolScale,
    // so in spine units the pitch is symbolWidth / symbolScale.
    if (this._anticipationBaseX === null) this._anticipationBaseX = bone.data.x;
    const middle = (Settings.REEL_COUNT - 1) / 2;
    const pitch = this.symbolWidth / this.symbolScale;
    bone.x = this._anticipationBaseX + (reelIndex - middle) * pitch;

    this._resetAnticipationSlots();
    const track = SPINE_CONTRACT.ANTICIPATION_TRACK;
    if (inClip) {
      state.setAnimation(track, inClip, false);
      state.addAnimation(track, loopClip, true, 0);
    } else {
      state.setAnimation(track, loopClip, true);
    }
  }

  /**
   * The standalone spine over `reelIndex`: its slot bone goes to the reel
   * window's centre (the reel slot's bone — both are children of `reels`),
   * stepped one reel pitch per reel, then in → loop.
   */
  _playStandaloneAnticipation(reelIndex) {
    const host = this._anticipationHost();
    const state = host.spine.state;
    const skeleton = this.spine?.skeleton;
    const bone = skeleton?.findSlot(this._anticipationSlotName)?.bone;
    const anchor = skeleton?.findSlot(this._firstSlotName(SPINE_CONTRACT.REEL_SLOTS))?.bone;
    const inClip = this._anticipationClip(host, SPINE_CONTRACT.ANTICIPATION_IN);
    const loopClip = this._anticipationClip(host, SPINE_CONTRACT.ANTICIPATION_LOOP);
    if (!state || !bone || !loopClip) return;

    const middle = (Settings.REEL_COUNT - 1) / 2;
    const pitch = this.symbolWidth / this.symbolScale;
    bone.x = (anchor ? anchor.x : bone.data.x) + (reelIndex - middle) * pitch;
    bone.y = anchor ? anchor.y : bone.data.y;

    state.clearTracks();
    host.spine.skeleton.setToSetupPose();
    host.spine.visible = true;
    if (inClip) {
      state.setAnimation(host.track, inClip, false);
      state.addAnimation(host.track, loopClip, true, 0);
    } else {
      state.setAnimation(host.track, loopClip, true);
    }
  }

  /**
   * Play anticipation_out on the reel it is on, then clear the track and hide
   * the clips' slots. No-op when nothing is playing.
   */
  stopAnticipation() {
    if (this.anticipationSpine) {
      const host = this._anticipationHost();
      const state = host.spine.state;
      if (!state?.getCurrent(host.track)) return;
      const outClip = this._anticipationClip(host, SPINE_CONTRACT.ANTICIPATION_OUT);
      if (!outClip) {
        this.hideAnticipation();
        return;
      }
      const entry = state.setAnimation(host.track, outClip, false);
      entry.listener = {
        complete: () => {
          if (state.getCurrent(host.track) !== entry) return;
          this.hideAnticipation();
        },
      };
      return;
    }
    const state = this.spine?.state;
    const track = SPINE_CONTRACT.ANTICIPATION_TRACK;
    if (!state?.getCurrent(track)) return;

    const outClip = this._firstAnimation(SPINE_CONTRACT.ANTICIPATION_OUT);
    if (!outClip) {
      this.hideAnticipation();
      return;
    }
    const entry = state.setAnimation(track, outClip, false);
    entry.listener = {
      complete: () => {
        if (state.getCurrent(track) !== entry) return;
        this.hideAnticipation();
      },
    };
  }

  /**
   * The three clips' lengths in ms, for ReelControlContext to time the chain
   * on. Missing clips come back undefined and the caller falls back.
   */
  getAnticipationTiming() {
    const host = this._anticipationHost();
    const ms = (candidates) => {
      const name = this._anticipationClip(host, candidates);
      const animation = name
        ? host.spine?.skeleton?.data?.findAnimation(name)
        : null;
      return animation ? Math.round(animation.duration * 1000) : undefined;
    };
    return {
      inMs: ms(SPINE_CONTRACT.ANTICIPATION_IN),
      loopMs: ms(SPINE_CONTRACT.ANTICIPATION_LOOP),
      outMs: ms(SPINE_CONTRACT.ANTICIPATION_OUT),
    };
  }

  /**
   * The reel anticipation spine, in the scene's `attachment_reel_anticipation`
   * slot. Same holder as attachAnticipation: spine-pixi rewrites the slot
   * object's own visible every frame, so the spine inside is shown / hidden.
   * Its clips key its bone back to 0,0, so it plays centred on the slot.
   */
  attachReelAnticipation() {
    const tag = this.constructor.SCENE.TAG;
    const slotName = this._firstSlotName(SPINE_CONTRACT.REEL_ANTICIPATION_SLOTS);
    if (!slotName) return;

    const { SKELETON, ATLAS } = SPINE_CONTRACT.REEL_ANTICIPATION_SPINE;
    const assets = PIXI.Assets.cache;
    if (!assets.has(SKELETON) || !assets.has(ATLAS)) {
      console.warn(`[${tag}] Reel anticipation spine not in cache`);
      return;
    }
    try {
      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });
      spine.name = "reel_anticipation";
      spine.visible = false;
      const holder = new PIXI.Container();
      holder.name = "reel_anticipation_slot_content";
      holder.addChild(spine);
      this.spine.addSlotObject(slotName, holder);
      this.reelAnticipationSpine = spine;
    } catch (error) {
      console.error(`[${tag}] Failed to create reel anticipation spine:`, error);
    }
  }

  _reelAnticipationClip(candidates) {
    const data = this.reelAnticipationSpine?.skeleton?.data;
    return candidates.find((name) => data?.findAnimation(name)) || null;
  }

  /** The chain has started: in → loop. Already up = left as it is. */
  playReelAnticipation() {
    const spine = this.reelAnticipationSpine;
    const state = spine?.state;
    if (!state) return;
    const current = state.getCurrent(0)?.animation?.name;
    const outClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_OUT);
    if (spine.visible && current && current !== outClip) return;

    const inClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_IN);
    const loopClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_LOOP);
    if (!loopClip) return;
    state.clearTracks();
    spine.skeleton.setToSetupPose();
    spine.visible = true;
    if (inClip) {
      state.setAnimation(0, inClip, false);
      state.addAnimation(0, loopClip, true, 0);
    } else {
      state.setAnimation(0, loopClip, true);
    }
  }

  /** The chain has ended: out, then hidden. No-op when it is not up. */
  stopReelAnticipation() {
    const spine = this.reelAnticipationSpine;
    const state = spine?.state;
    if (!state?.getCurrent(0) || !spine.visible) return;
    const outClip = this._reelAnticipationClip(SPINE_CONTRACT.REEL_ANTICIPATION_OUT);
    if (!outClip) {
      this.hideReelAnticipation();
      return;
    }
    if (state.getCurrent(0).animation?.name === outClip) return;
    const entry = state.setAnimation(0, outClip, false);
    entry.listener = {
      complete: () => {
        if (state.getCurrent(0) !== entry) return;
        this.hideReelAnticipation();
      },
    };
  }

  /** Instant: clear it and hide it (a new round, a room change). */
  hideReelAnticipation() {
    const spine = this.reelAnticipationSpine;
    if (!spine) return;
    spine.state?.clearTracks();
    spine.skeleton?.setToSetupPose();
    spine.visible = false;
  }

  /** Instant: clear the track and hide every anticipation slot. */
  hideAnticipation() {
    if (this.anticipationSpine) {
      const spine = this.anticipationSpine;
      spine.state?.clearTracks();
      spine.skeleton?.setToSetupPose();
      spine.visible = false;
      return;
    }
    const state = this.spine?.state;
    if (!state) return;
    state.clearTrack(SPINE_CONTRACT.ANTICIPATION_TRACK);
    this._resetAnticipationSlots();
  }

  /**
   * Put the clips' slots back to setup — in the export none of them has an
   * attachment there, so this hides them. Needed because a clip cut short
   * (clearTrack, or `in` replacing `loop`) leaves its last attachments on.
   */
  _resetAnticipationSlots() {
    const prefix = SPINE_CONTRACT.ANTICIPATION_SLOT_PREFIX;
    for (const slot of this.spine?.skeleton?.slots ?? []) {
      if (slot.data.name.startsWith(prefix)) slot.setToSetupPose();
    }
  }

  // ==========================================================================
  // SCALING
  // ==========================================================================

  applyResponsiveScaling() {
    if (!this.dimensions || !this.spine) return;

    const { width, height } = this.dimensions;
    const offset = resolveOffset(this.layout);
    const zoom = resolveZoom(this.layout);

    this.spine.scale.set(this.symbolScale * zoom);
    this.spine.position.set(
      // Y is SUBTRACTED so a positive value pushes the scene up.
      width / 2 + this.symbolHeight * offset.x,
      height / 2 - this.symbolHeight * offset.y,
    );
  }

  resize(
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
  ) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.layout = layout;

    this.applyResponsiveScaling();
    this.setOrientation(orientation);
    this.applyReelBlockScale();
  }

  /**
   * Scale the reel block inside the scene.
   *
   * Safe to set once and leave: the idle animation does not key the `reels`
   * bone, and spine only writes bones an animation actually touches — so the
   * value is not overwritten every frame. It is re-applied on resize anyway,
   * because the layout (and therefore the value) can change.
   *
   * The injection point is a CHILD of this bone, so the reels scale with the
   * frame automatically and the two can never drift apart.
   */
  applyReelBlockScale() {
    const bone = this._firstBone(SPINE_CONTRACT.REEL_BLOCK_BONES);
    if (!bone) return;
    const scale = resolveReelScale(this.layout);
    bone.scaleX = scale;
    bone.scaleY = scale;
  }

  // ==========================================================================
  // INJECTION — the two slots this game actually needs
  // ==========================================================================

  attachSlotContainers() {
    if (!this.spine?.skeleton) return;

    // Reels first, so they are behind the overlay if both land on the same slot.
    this.reelSlotContainer = this._attachContainer({
      name: "gameview_reel_slot_content",
      registryKey: this.constructor.SCENE.REEL_SET_KEY,
      slotNames: SPINE_CONTRACT.REEL_SLOTS,
      boneNames: SPINE_CONTRACT.REEL_BONES,
      behindSlotNames: SPINE_CONTRACT.REEL_BEHIND_SLOTS,
    });

    this.overlayReelSlotContainer = this._attachContainer({
      name: "gameview_overlay_reel_slot_content",
      registryKey: this.constructor.SCENE.OVERLAY_REEL_SET_KEY,
      slotNames: SPINE_CONTRACT.OVERLAY_SLOTS,
    });

    // ── The coins' fire trails fly HERE — just under the logo ──
    // `coin_trail_placeholder` (hand-added, on an identity bone at the root)
    // sits right before `logo_placeholder`: above the reels, the overlay and
    // the jackpot plates, below only the logo — so a trail slips behind the
    // logo as it lands and reads as falling into the bowl.
    const trailSlot = this._firstSlotName(["coin_trail_placeholder"]);
    if (trailSlot) {
      const layer = new PIXI.Container();
      layer.name = "coin_trail_layer";
      layer.sortableChildren = true;
      this.spine.addSlotObject(trailSlot, layer);
      this.coinTrailContainer = layer;
    }
  }

  /**
   * Put the logo spine into the scene's logo slot and loop its idle, with the
   * stage-1 fire on its own track.
   *
   * A slot object, so spine copies the slot bone's transform onto it every
   * frame — the orientation skins move and scale the logo with no code here.
   */
  attachLogo() {
    const tag = this.constructor.SCENE.TAG;
    const slotName = this._logoSlotName();
    if (!slotName) {
      console.warn(`[${tag}] No logo slot`, SPINE_CONTRACT.LOGO_SLOTS);
      return;
    }

    const assets = PIXI.Assets.cache;
    if (!assets.has(LOGO_ASSETS.SKELETON) || !assets.has(LOGO_ASSETS.ATLAS)) {
      console.warn(`[${tag}] Logo spine not in cache`);
      return;
    }

    try {
      const logo = Spine.from({
        skeleton: LOGO_ASSETS.SKELETON,
        atlas: LOGO_ASSETS.ATLAS,
      });
      logo.name = "logo";
      // Every change of clip on the logo blends instead of cutting.
      logo.state.data.defaultMix = LOGO_ASSETS.LEVEL_MIX;

      const firstAnimation = (candidates) =>
        candidates.find((name) => logo.skeleton.data.findAnimation(name));

      // One level clip, looped — it has the idle in it.
      // const level = firstAnimation(LOGO_ASSETS.LEVEL_ANIMATIONS);
      // if (level) logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, level, true);
      // The Pot's CURRENT fire (logoPot, restored from localStorage), without
      // its `_in` — the room opens on it already burning.
      const level =
        this._logoLevelClip(logo, logoPotStage()) ??
        firstAnimation(LOGO_ASSETS.LEVEL_ANIMATIONS);
      if (level) logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, level, true);
      this._logoStage = Math.min(logoPotStage(), LOGO_POT_STAGES);
      // Every stage change from here on — this room and the other share it.
      this._unsubscribeLogoPot?.();
      this._unsubscribeLogoPot = onLogoPotChange((stage, animate) =>
        this.setLogoStage(stage, animate),
      );
      // Was: idle on its own track + the fire level on another.
      // const idle = firstAnimation(LOGO_ASSETS.IDLE_ANIMATIONS);
      // if (idle) logo.state.setAnimation(LOGO_ASSETS.IDLE_TRACK, idle, true);
      // const fire = firstAnimation(LOGO_ASSETS.FIRE_ANIMATIONS);
      // if (fire) logo.state.setAnimation(LOGO_ASSETS.FIRE_TRACK, fire, true);

      this.spine.addSlotObject(slotName, logo);
      this.logoSpine = logo;
    } catch (error) {
      console.error(`[${tag}] Failed to create logo spine:`, error);
    }
  }

  /**
   * Create a container, put it in the spine at the right place, and publish it
   * to the SlotRegistry so the React side can inject into it.
   *
   * Two placement mechanics, and they are not interchangeable:
   *
   *   SLOT (preferred) — spine's addSlotObject syncs the transform AND sets
   *     zIndex from the slot's draw-order position every frame. The artist
   *     decides the layering; we inherit it and have nothing to tune.
   *
   *   BONE (fallback) — for a skeleton that marks the spot with a bare bone.
   *     The transform is copied by hand and the zIndex is forced to sit just
   *     after a named slot, because a bone carries no draw-order information.
   *
   * @returns {PIXI.Container|null}
   */
  _attachContainer({
    name,
    registryKey,
    slotNames,
    boneNames,
    behindSlotNames,
  }) {
    const container = new PIXI.Container();
    container.name = name;
    container.sortableChildren = true;

    const slotName = slotNames ? this._firstSlotName(slotNames) : null;

    if (slotName) {
      this.spine.addSlotObject(slotName, container);
    } else if (boneNames) {
      const bone = this._firstBone(boneNames);
      if (!bone) {
        console.warn(`[${this.constructor.SCENE.TAG}] No slot or bone found for ${name}`, {
          slotNames,
          boneNames,
        });
        return null;
      }
      this._attachToBone(container, bone, behindSlotNames);
    } else {
      console.warn(
        `[${this.constructor.SCENE.TAG}] Slot not found for ${name}`,
        slotNames,
      );
      return null;
    }

    if (
      this._registerSlot &&
      FEATURES_CONFIG.SLOT_REGISTRY.GAME_VIEW_INJECTION
    ) {
      this._registerSlot(registryKey, container);
    }

    return container;
  }

  /**
   * Fallback placement: add as a plain child and copy a bone's transform every
   * frame, with a forced zIndex.
   */
  _attachToBone(container, bone, behindSlotNames) {
    const behindSlot = behindSlotNames
      ? this._firstSlotName(behindSlotNames)
      : null;

    // +0.5 lands between two integer slot indices, so it never collides with a
    // real slot's own draw position.
    container.zIndex = behindSlot
      ? this._getSlotIndex(behindSlot) + 0.5
      : this.spine.skeleton.slots.length;

    this.spine.addChild(container);

    this._reelBone = bone;
    this.spine.afterUpdateWorldTransforms = () => {
      this._syncBoneToContainer(this._reelBone, container);
    };
  }

  /**
   * Copy a bone's world transform onto a container.
   *
   * The same thing spine-pixi's own updateSlotObject does, minus the zIndex
   * override — which is the entire point, because the forced zIndex is what
   * keeps the reels under the frame.
   */
  _syncBoneToContainer(bone, container) {
    if (!bone || !container || container.destroyed) return;
    const transform = container.localTransform;
    transform.a = bone.a;
    transform.b = bone.c;
    transform.c = -bone.b;
    transform.d = -bone.d;
    transform.tx = bone.worldX;
    transform.ty = bone.worldY;
    container.transform.setFromMatrix(transform);
  }

  // ==========================================================================
  // CLEANUP
  // ==========================================================================

  /**
   * Where a coin's trail lands: dead centre of the logo across (its origin —
   * the logo is centred on it), at the height of the FIRE on top (its fire
   * bone, live — it follows the logo in both layouts), in GLOBAL space. The
   * logo's centre if the bone is missing. Null without a logo.
   */
  getLogoTarget() {
    const logo = this.logoSpine;
    if (!logo || logo.destroyed || !logo.parent) return null;
    const skeleton = logo.skeleton;
    const bone = LOGO_ASSETS.FIRE_BONES.map((name) => skeleton?.findBone(name)).find(
      Boolean,
    );
    return logo.toGlobal(new PIXI.Point(0, bone ? bone.worldY : 0));
  }

  /**
   * The jackpot plates' values — money text in each `txt_slot_jackpot_<tier>`
   * slot (hand-added over the plates' numbers), built on first use like the
   * coin counter. Slot objects, so the plates' bones place and scale them in
   * both layouts. Never pixel-rounded: the spine scales them.
   *
   * @param {Object<string, number>} values tier → money (subunits)
   */
  setJackpotValues(values) {
    if (!this.spine?.skeleton || !values) return;
    if (!this._jackpotTexts) this._jackpotTexts = {};
    for (const [tier, value] of Object.entries(values)) {
      // New game view: txt_slot_jckpt_<tier>; the older spines: txt_slot_jackpot_<tier>.
      const slotName = this._firstSlotName([
        `txt_slot_jckpt_${tier}`,
        `txt_slot_jackpot_${tier}`,
      ]);
      if (!slotName) continue;

      const plate = JACKPOT_PLATE_TEXT_BY_TIER[tier] ?? JACKPOT_PLATE_TEXT;
      let text = this._jackpotTexts[tier];
      if (!text || text.destroyed) {
        const container = new PIXI.Container();
        container.name = `jackpot_value_${tier}`;
        text = new PIXI.Text(
          "",
          // new PIXI.TextStyle(scaleStyle(FONT_STYLES.winText, JACKPOT_PLATE_TEXT.FONT_SIZE)),
          // The plates' golden look (fontStyles `jackpots`, 2026-09-30).
          // new PIXI.TextStyle(scaleStyle(FONT_STYLES.jackpots, JACKPOT_PLATE_TEXT.FONT_SIZE)),
          new PIXI.TextStyle(scaleStyle(FONT_STYLES.jackpots, plate.FONT_SIZE)),
        );
        text.anchor.set(0.5);
        text.resolution = 2;
        text.roundPixels = false;
        container.addChild(text);
        this.spine.addSlotObject(slotName, container);
        this._jackpotTexts[tier] = text;
      }

      const label = formatToCustomLocale(value);
      if (text.text !== label) text.text = label;
      text.scale.set(1);
      // if (text.width > JACKPOT_PLATE_TEXT.MAX_WIDTH) {
      //   text.scale.set(JACKPOT_PLATE_TEXT.MAX_WIDTH / text.width);
      // }
      if (text.width > plate.MAX_WIDTH) text.scale.set(plate.MAX_WIDTH / text.width);
    }
  }

  /**
   * The level clip for a fire stage — `logo_lvl<stage>`, falling back to the
   * nearest lower one the rig has (Zeus PotController._resolveAnim). WIN has
   * no clip of its own yet (animator: the blast), so it shows the top stage.
   */
  _logoLevelClip(logo, stage, suffix = "") {
    const data = logo?.skeleton?.data;
    if (!data) return null;
    for (let s = Math.min(stage, LOGO_POT_STAGES); s >= 1; s--) {
      const name = `logo_lvl${s}${suffix}`;
      if (data.findAnimation(name)) return name;
    }
    return null;
  }

  /**
   * Show fire stage `stage` (GDD "POT upgrade stages") — Zeus PotController.
   * setStage, on this rig's clips: `logo_lvl<n>_in` once, then `logo_lvl<n>`
   * looped. Without `animate` (a reset, a room opening) it cuts straight to
   * the loop. LOGO_POT_WIN: the top stage until the blast clip exists.
   */
  setLogoStage(stage, animate = true) {
    const logo = this.logoSpine;
    if (!logo?.state || logo.destroyed) return;
    // // WIN is the blast, over the fire as it stands — no level change.
    // if (stage === LOGO_POT_WIN) {
    //   this.playLogoBlast();
    //   return;
    // }
    // ── WIN (GDD "POT upgrade stages": 4 stages, "4th stage is a WIN
    // stage", then "POT should play small blast animation") ── the fire goes
    // up to stage 4 first, then the blast (Vladimir, 2026-09-30: "make sure
    // we follow gdd"). From stage 3 that is lvl4_in — the blast at its end;
    // from lower, a blend into lvl4 — the blast when the blend is done. The
    // blast's start is a spine delay on its own track, not a timer.
    if (stage === LOGO_POT_WIN) {
      const from = this._logoStage ?? 1;
      if (from >= LOGO_POT_STAGES) {
        this.playLogoBlast();
        return;
      }
      const top = this._logoLevelClip(logo, LOGO_POT_STAGES);
      if (!top) {
        this.playLogoBlast();
        return;
      }
      this._logoStage = LOGO_POT_STAGES;
      const topIn = logo.skeleton.data.findAnimation(`${top}_in`);
      let rise;
      if (animate && from === LOGO_POT_STAGES - 1 && topIn) {
        logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, `${top}_in`, false);
        logo.state.addAnimation(LOGO_ASSETS.LEVEL_TRACK, top, true, 0);
        rise = topIn.duration;
      } else {
        logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, top, true);
        rise = animate ? LOGO_ASSETS.LEVEL_MIX : 0;
      }
      this.playLogoBlast(rise);
      return;
    }
    const level = this._logoLevelClip(logo, stage);
    if (!level) return;
    // TODO(animator): the WIN blast (GDD: "small blast animation around the
    // fire and logo") plays here when it is delivered.
    // `_in` only for ONE step up — each is built from the stage below it
    // (lvl4_in starts on lvl3's fire). A jump blends straight into the loop.
    const shown = Math.min(stage, LOGO_POT_STAGES);
    const oneStepUp = shown === (this._logoStage ?? 1) + 1;
    this._logoStage = shown;
    const inClip =
      animate && oneStepUp && logo.skeleton.data.findAnimation(`${level}_in`)
        ? `${level}_in`
        : null;
    if (inClip) {
      logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, inClip, false);
      logo.state.addAnimation(LOGO_ASSETS.LEVEL_TRACK, level, true, 0);
    } else {
      logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, level, true);
    }
  }

  /**
   * A jackpot coin landed: its plate's `jackpot_<tier>_hit` once, on its OWN
   * track above the room's idle (Vladimir, 2026-09-30: "a separate
   * animation"), then released. One track per tier, so two jackpots landing
   * together both play. A room whose spine has no such clip does nothing.
   * @param {string} tier mini | minor | midi | major | grand | royal
   */
  playJackpotHit(tier) {
    const state = this.spine?.state;
    const clip = `jackpot_${tier}_hit`;
    if (!state || !this.spine.skeleton?.data?.findAnimation(clip)) return;
    const index = JACKPOT_HIT_TIERS.indexOf(tier);
    if (index < 0) return;
    const track = JACKPOT_HIT_TRACK_BASE + index;
    // A fade-out still running on this plate stops — the clip lights it again.
    this._jackpotHitFades?.get(track)?.();
    state.setAnimation(track, clip, false);
    // state.addEmptyAnimation(track, 0.1, 0);
    // ── The plate STAYS lit ── the clip is only a 0.17 s fade-in of the
    // plate's screen copy to full, then it holds — released at once, it was
    // a flash nobody saw (Vladimir, 2026-09-30: "barely visible"). It holds
    // on its last frame until the next spin (releaseJackpotHits).
  }

  /**
   * The next spin: every lit plate fades back out.
   *
   * Not setEmptyAnimation: an empty mixes the clip back to the SETUP pose,
   * and the glow slot's setup colour is full white — it stayed lit
   * (Vladimir, 2026-09-30: "you now dont turn it off at all"). So the track
   * is let go, the glow slots the clip keys fade to 0 on the ticker, and
   * their attachment comes off (the setup has none).
   */
  releaseJackpotHits() {
    const state = this.spine?.state;
    const skeleton = this.spine?.skeleton;
    if (!state || !skeleton) return;
    if (!this._jackpotHitFades) this._jackpotHitFades = new Map();
    JACKPOT_HIT_TIERS.forEach((tier, index) => {
      const track = JACKPOT_HIT_TRACK_BASE + index;
      if (!state.tracks?.[track]) return;
      state.clearTrack(track);
      const clip = skeleton.data.findAnimation(`jackpot_${tier}_hit`);
      const slots = [
        ...new Set(
          (clip?.timelines || [])
            .map((t) => t.slotIndex)
            .filter((i) => Number.isInteger(i)),
        ),
      ]
        .map((i) => skeleton.slots[i])
        .filter(Boolean);
      if (!slots.length) return;
      const from = slots.map((slot) => slot.color.a);
      let elapsed = 0;
      const total = JACKPOT_HIT_RELEASE_S * 1000;
      const stop = () => {
        PIXI.Ticker.shared.remove(tick);
        this._jackpotHitFades.delete(track);
      };
      const tick = () => {
        elapsed += PIXI.Ticker.shared.deltaMS;
        const k = Math.max(0, 1 - elapsed / total);
        slots.forEach((slot, i) => {
          slot.color.a = from[i] * k;
        });
        if (k > 0) return;
        slots.forEach((slot) => slot.setAttachment(null));
        stop();
      };
      this._jackpotHitFades.get(track)?.();
      this._jackpotHitFades.set(track, stop);
      PIXI.Ticker.shared.add(tick);
    });
  }

  /**
   * The Pot's WIN (Hold & Win triggering): `logo_blast` once on its own track
   * above the level clip, released at its end (as the hit is).
   */
  // `delay` (s): the blast waits that long on its track first — the fire's
  // rise to stage 4 (setLogoStage WIN).
  playLogoBlast(delay = 0) {
    const logo = this.logoSpine;
    if (!logo?.state || logo.destroyed) return;
    const blast = LOGO_ASSETS.BLAST_ANIMATIONS.find((name) =>
      logo.skeleton.data.findAnimation(name),
    );
    if (!blast) return;
    // const entry = logo.state.setAnimation(LOGO_ASSETS.BLAST_TRACK, blast, false);
    let entry;
    if (delay > 0) {
      logo.state.clearTrack(LOGO_ASSETS.BLAST_TRACK);
      entry = logo.state.addAnimation(LOGO_ASSETS.BLAST_TRACK, blast, false, delay);
    } else {
      entry = logo.state.setAnimation(LOGO_ASSETS.BLAST_TRACK, blast, false);
    }
    logo.state.addEmptyAnimation(
      LOGO_ASSETS.BLAST_TRACK,
      0.1,
      entry?.animation?.duration ?? 0,
    );
    // Its end, for whoever sequences after it (the Random Pot).
    this._logoBlastDone = new Promise((resolve) => {
      entry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  /** Resolves when the last blast has played out (at once if none is on). */
  logoBlastDone() {
    return this._logoBlastDone ?? Promise.resolve();
  }

  /**
   * The Random Pot's logo over the reels (GDD: "Animate the game logo over
   * the reel area, briefly covering the reels for approximately 1–2
   * seconds"): the room's `logo_reel_landscape` / `logo_reel_portrait` moves
   * the logo's slot onto the reels and back, while the logo plays
   * `logo_lvl<stage>_reel` (the same 2 s) and then returns to its level loop.
   * Resolves when the room's clip has played out — the moment the reels may
   * stop. At once when the room has no such clip.
   */
  playLogoReelFlight() {
    const state = this.spine?.state;
    const data = this.spine?.skeleton?.data;
    const portrait = (this._logoSlotName() || "").includes("portrait");
    const flight = portrait ? "logo_reel_portrait" : "logo_reel_landscape";
    if (!state || !data?.findAnimation(flight)) return Promise.resolve();

    const logo = this.logoSpine;
    const stage = Math.min(logoPotStage(), LOGO_POT_STAGES);
    const level = logo ? this._logoLevelClip(logo, stage) : null;
    const reelClip = level ? `${level}_reel` : null;
    if (logo?.state && reelClip && logo.skeleton.data.findAnimation(reelClip)) {
      logo.state.setAnimation(LOGO_ASSETS.LEVEL_TRACK, reelClip, false);
      logo.state.addAnimation(LOGO_ASSETS.LEVEL_TRACK, level, true, 0);
    }

    const entry = state.setAnimation(LOGO_REEL_TRACK, flight, false);
    state.addEmptyAnimation(LOGO_REEL_TRACK, 0.1, entry?.animation?.duration ?? 0);
    return new Promise((resolve) => {
      entry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  /** The cup takes a hit: `logo_hit` once on its own track, then released. */
  playLogoHit() {
    const logo = this.logoSpine;
    if (!logo?.state || logo.destroyed) return;
    const hit = LOGO_ASSETS.HIT_ANIMATIONS.find((name) =>
      logo.skeleton.data.findAnimation(name),
    );
    if (!hit) return;
    const entry = logo.state.setAnimation(LOGO_ASSETS.HIT_TRACK, hit, false);
    // logo.state.addEmptyAnimation(LOGO_ASSETS.HIT_TRACK, 0.1, 0);
    // Released AT the clip's end, not 0.1 s before it: delay 0 means
    // "duration minus the mix", which started the release while the glows
    // were still ~60% bright and cut them to nothing — the abrupt ending,
    // worst on the last of several hits (Vladimir, 2026-09-30). logo_hit ends
    // at rest (glows 0, scale 1), so a release there is invisible. A new hit
    // meanwhile replaces this one and queues its own release.
    logo.state.addEmptyAnimation(
      LOGO_ASSETS.HIT_TRACK,
      0.1,
      entry?.animation?.duration ?? 0,
    );
  }

  destroy() {
    // A plate still fading out must not tick on a destroyed skeleton.
    [...(this._jackpotHitFades?.values() || [])].forEach((stop) => stop());
    if (this._unregisterSlot) {
      this._unregisterSlot(this.constructor.SCENE.REEL_SET_KEY);
      this._unregisterSlot(this.constructor.SCENE.OVERLAY_REEL_SET_KEY);
    }

    this._unsubscribeLogoPot?.();
    this._unsubscribeLogoPot = null;
    if (this.logoSpine) {
      this.logoSpine.destroy();
      this.logoSpine = null;
    }

    if (this.anticipationSpine) {
      this.anticipationSpine.destroy();
      this.anticipationSpine = null;
    }

    if (this.reelAnticipationSpine) {
      this.reelAnticipationSpine.destroy();
      this.reelAnticipationSpine = null;
    }

    if (this.spine) {
      this.spine.afterUpdateWorldTransforms = null;
      this.spine.destroy();
      this.spine = null;
    }

    this.reelSlotContainer = null;
    this.overlayReelSlotContainer = null;
    this._reelBone = null;
  }
}

export default GameViewSimpleController;
